import axios from 'axios';
import type mapboxgl from 'mapbox-gl';
import getConfig from '~/config';

const esriGeocodeServerUrl = 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer';

export async function getAddressSuggestions(
  text: string,
  center?: mapboxgl.LngLat,
): Promise<{ magicKey: string; text: string }[]> {
  if (text.length < 3) {
    return [];
  }

  // TODO: Use browser location
  let location = '-122.42249092112374,37.80198798745311';

  if (center?.lng && center.lat) {
    location = `${center.lng},${center.lat}`;
  }

  const returnFormat = '&f=json';
  const countryCode = 'USA';
  const encodedText = encodeURIComponent(text);
  const optionalParameters = `&text=${encodedText}&location=${location}&countryCode=${countryCode}&sourceCountry=${countryCode}`;
  const apiKey = getConfig().esriToken;
  const accessToken = `token=${apiKey}`;
  const completeUrl = `${esriGeocodeServerUrl}/suggest?${accessToken}${returnFormat}${optionalParameters}`;

  const { data } = await axios({
    method: 'get',
    url: completeUrl,
  });
  return data.suggestions;
}

export async function getAddressDetail(addressString: string, magicKey: string) {
  const returnFormat = '&f=json';
  const completeUrl = `${esriGeocodeServerUrl}/findAddressCandidates?SingleLine=${addressString}&magicKey=${magicKey}${returnFormat}`;

  const response = await axios({
    method: 'get',
    url: completeUrl,
  });
  const { location, address } = response.data.candidates[0];

  return {
    location,
    address,
  };
}

export const validateCoordinates = (coord: string): boolean => {
  // Regular expression to validate the numerical format, including optional leading '-'
  const numberPattern = /^-?\d+(\.\d+)?$/;
  // Split the string into longitude and latitude
  const parts = coord.split(',');

  // Ensure the coordinate is a pair
  if (parts.length !== 2) {
    return false;
  }

  const latitude = parts[0].trim();
  const longitude = parts[1].trim();

  // Validate format of longitude and latitude
  if (!numberPattern.test(longitude) || !numberPattern.test(latitude)) {
    return false;
  }

  // Parse to numbers
  const lon = parseFloat(longitude);
  const lat = parseFloat(latitude);

  // Check the ranges
  return lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90;
};

export async function getStreetAndIntersection(lng: number, lat: number) {
  const apiKey = getConfig().esriToken;
  const intersection = await axios({
    method: 'GET',
    url: `${esriGeocodeServerUrl}/reverseGeocode`,
    params: {
      token: apiKey,
      f: 'json',
      location: `${lng},${lat}`,
      featureTypes: 'StreetInt',
      cacheBust: Math.random(),
    },
  });

  const streetAddress = await axios({
    method: 'GET',
    url: `${esriGeocodeServerUrl}/reverseGeocode`,
    params: {
      token: apiKey,
      f: 'json',
      location: `${lng},${lat}`,
      featureTypes: '',
      cacheBust: Math.random(),
    },
  });

  // ArcGIS answers 200 with an error body and no address when nothing of the requested type is
  // near the location, which is routine for StreetInt outside towns. Read each lookup on its own
  // so one empty result still leaves the other's fields.
  const streetMatch = streetAddress.data.address;
  const intersectionMatch = intersection.data.address;

  return {
    // A POI candidate's labels carry the business name; Address holds the street address for
    // every candidate type.
    street: streetMatch?.Address ?? '',
    intersection: intersectionMatch?.ShortLabel ?? '',
    city: streetMatch?.City ?? '',
    state: streetMatch?.RegionAbbr ?? '',
    zip: streetMatch?.Postal ?? '',
  };
}
